import { Component, ChangeDetectorRef, Input, Output, EventEmitter, OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';
import { VendorOrders } from '@models/vendor-order';
import { LogElement } from '@models/order';
import { Store } from '@models/store';
import { VendorOrderService } from '@services/vendor-order.service';
import { AuthService } from '@services/auth.service';
import { RentService } from '@services/rent.service';
import { ProductService } from '@services/product.service';
import { ImageService } from '@services/image.service';
import { ActivatedRoute } from '@angular/router';

export interface BorrowFrameRow {
  productId: string;
  productStore: any;
  model: string;
  color: string;
  size: string;
  type: string;
  borrowed: string | number;
  deposit: number;
  landedCost: number | null;
  amountDue: number | null;
  orderNumber: string;
  initials: string;
  status: 'loan' | 'due' | 'done';
  imageId: string | null;
  rentId?: string;
}

interface BorrowVendorGroup extends VendorOrders {
  rows: BorrowFrameRow[];
}

const IMAGE_BATCH_SIZE = 100;

@Component({
  selector: 'app-borrowing-vendors',
  templateUrl: './borrowing-vendors.component.html',
  styleUrls: ['./borrowing-vendors.component.css', '../../base/vendors-base/vendors-base.component.css', '../../managed-board.component.css']
})
export class BorrowingVendorsComponent implements OnChanges, AfterViewInit {
  @Input() rentingUser: string;
  @Input() borrowingStore: string;
  @Input() locationPhrase = '';
  @Input() returnLabel = 'Send Back';
  @Input() returnStoreName = '';
  @Output() framesChange = new EventEmitter<BorrowFrameRow[]>();

  vendors: BorrowVendorGroup[] = [];
  dataLoading = false;
  imageUrls: Record<string, string> = {};
  currentStore: Store = new Store();
  private imageLoadRequestId = 0;

  constructor(
    public vendorOrderService: VendorOrderService,
    public auth: AuthService,
    public cdRef: ChangeDetectorRef,
    public route: ActivatedRoute,
    private rentService: RentService,
    private productService: ProductService,
    private imageService: ImageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.borrowingStore && changes.borrowingStore.currentValue != null) {
      this.getVendorOrders();
    }

    if (changes.rentingUser && changes.rentingUser.currentValue) {
      this.getRents();
    }
  }

  ngAfterViewInit() {
    this.currentStore = this.auth.getStoreObject();
    this.cdRef.detectChanges();
  }

  hideImage(event: Event): void {
    (event.target as HTMLImageElement).style.display = 'none';
  }

  recordLabel(count: number): string {
    return count === 1 ? '1 active record' : `${count || 0} active records`;
  }

  getVendorOrders() {
    this.dataLoading = true;
    this.vendorOrderService.getBorrowingVendorOrders(this.borrowingStore).subscribe((vendorOrders: VendorOrders[]) => {
      const groups = (vendorOrders ?? []).map(group => this.withBorrowRows(group));
      this.vendors = groups;
      this.publish(groups);
      this.dataLoading = false;
      this.cdRef.detectChanges();
    }, () => {
      this.vendors = [];
      this.publish([]);
      this.dataLoading = false;
    });
  }

  getRents() {
    this.dataLoading = true;
    this.rentService.fetchRents(this.rentingUser, this.auth.getStore()).subscribe(rents => {
      const groupedRents = (rents || []).reduce((arr, rent) => {
        const vendorId = rent.product?.vendor?._id;
        if (!vendorId) {
          return arr;
        }
        if (!arr[vendorId]) {
          arr[vendorId] = {
            vendor: rent.product.vendor,
            count: 0,
            rentingProducts: []
          };
        }
        arr[vendorId].count += rent.qty;
        arr[vendorId].rentingProducts.push({
          ...rent.product,
          quantity: rent.qty,
          rent: rent._id
        });
        return arr;
      }, {});
      const groups = Object.keys(groupedRents).map(vendorId => this.withRentRows({
        _id: vendorId,
        vendor: groupedRents[vendorId].vendor,
        rentingProducts: groupedRents[vendorId].rentingProducts,
        count: groupedRents[vendorId].count
      } as VendorOrders));
      this.vendors = groups;
      this.publish(groups);
      this.dataLoading = false;
      this.cdRef.detectChanges();
    }, () => {
      this.vendors = [];
      this.publish([]);
      this.dataLoading = false;
    });
  }

  returnFrame(event: Event, row: BorrowFrameRow) {
    const logItem = new LogElement();
    logItem.initials = this.auth.currentUser?.initials;
    logItem.user = this.auth.currentUser?._id;
    logItem.item = 'RETURN BORROWED FRAME FROM STORE ' + this.borrowingStore;
    logItem.notes = '';
    logItem.date = Date.now().toString();

    const borrowingStore = (this.borrowingStore == '0') ? this.auth.getStore() : this.borrowingStore;
    const borrowedFromStore = (this.borrowingStore == '0') ? row.productStore : this.auth.getStore();

    this.productService.returnBorrowedProduct(row.productId, borrowingStore, 1, borrowedFromStore, logItem).subscribe(() => {
      this.getVendorOrders();
    });
    event.preventDefault();
    event.stopPropagation();
  }

  returnRent(event: Event, row: BorrowFrameRow) {
    this.rentService.returnProduct(row.rentId).subscribe(() => {
      this.getRents();
    });
    event.preventDefault();
    event.stopPropagation();
  }

  private withBorrowRows(group: VendorOrders): BorrowVendorGroup {
    const rows: BorrowFrameRow[] = [];
    for (const order of group.vendorOrders || []) {
      const initials = this.initialsFrom(order.log);
      const sold = (order.orders || []).length > 0;
      for (const line of order.products || []) {
        const product = line?.product;
        if (!product || typeof product === 'string') {
          continue;
        }
        const landed = this.money(line.cost ?? product.cost);
        const status = sold ? 'due' : 'loan';
        rows.push({
          productId: product._id,
          productStore: product.store,
          model: product.model || '',
          color: product.color || '',
          size: product.size || '',
          type: product.type || line.type || '',
          borrowed: order.date || '',
          deposit: Number(line.frame?.deposit) || 0,
          landedCost: landed,
          amountDue: status === 'due' ? landed : null,
          orderNumber: order.invoiceNumber || '',
          initials,
          status,
          imageId: product.images?.[0] || null
        });
      }
    }
    return { ...group, count: rows.length, rows };
  }

  private withRentRows(group: VendorOrders): BorrowVendorGroup {
    const rows = (group.rentingProducts || []).map(product => ({
      productId: product._id,
      productStore: product.store,
      model: product.model || '',
      color: product.color || '',
      size: product.size || '',
      type: product.type || '',
      borrowed: product.dateAdded || '',
      deposit: 0,
      landedCost: this.money(product.cost),
      amountDue: null,
      orderNumber: '',
      initials: '',
      status: 'loan' as const,
      imageId: product.images?.[0] || null,
      rentId: (product as any).rent
    }));
    return { ...group, count: rows.length, rows };
  }

  private initialsFrom(log: LogElement[] = []): string {
    for (let index = log.length - 1; index >= 0; index--) {
      const initials = (log[index]?.initials || '').trim();
      if (initials) {
        return initials;
      }
    }
    return '';
  }

  private money(value: number | null | undefined): number | null {
    if (value === null || value === undefined || value === ('' as any)) {
      return null;
    }
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : null;
  }

  private publish(groups: BorrowVendorGroup[]) {
    const rows = groups.reduce((all, group) => all.concat(group.rows), [] as BorrowFrameRow[]);
    this.framesChange.emit(rows);
    this.loadImages(rows);
  }

  private loadImages(rows: BorrowFrameRow[]) {
    const requestId = ++this.imageLoadRequestId;
    const missingIds: string[] = [];
    rows.forEach(row => {
      if (row.imageId && !this.imageUrls[row.imageId] && !missingIds.includes(row.imageId)) {
        missingIds.push(row.imageId);
      }
    });
    if (!missingIds.length) {
      return;
    }

    const width = this.imageService.getThumbnailWidth('product');
    const loadBatch = (index: number) => {
      if (index >= missingIds.length || requestId !== this.imageLoadRequestId) {
        return;
      }
      const batch = missingIds.slice(index, index + IMAGE_BATCH_SIZE);
      this.imageService.getBulkThumbnailUrls(batch, width).subscribe((urls: Record<string, string>) => {
        if (requestId !== this.imageLoadRequestId) {
          return;
        }
        this.imageUrls = { ...this.imageUrls, ...(urls || {}) };
        this.cdRef.detectChanges();
        loadBatch(index + IMAGE_BATCH_SIZE);
      });
    };
    loadBatch(0);
  }
}
